// Small "Cold chain" card for a device: current values + a 6 h mini chart of its first cold point.
// Read-only and shown to every org member (setup lives in the Cold points tab).
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { coldChain } from '@/lib/trackizy';
import useFeatures from '@/hooks/useFeatures';
import ColdStatusChip from './ColdStatusChip';
import ColdPointChart from './ColdPointChart';
import { formatTemp } from './coldChainModel';
import { useColdChainShared } from './ColdChainShared';
import ColdPointDrawer from './ColdPointDrawer';
import useCanManageColdChain from './useCanManageColdChain';

const REFRESH_MS = 60_000;

// The same drawer the Cold Chain page opens. Portaled to <body>: the panels it is opened from are
// frosted (backdrop-filter), which would otherwise trap its `fixed` overlay inside the panel.
function DrawerHost({ pointId, onClose, onSaved }) {
  const canManage = useCanManageColdChain();
  return createPortal(<ColdPointDrawer pointId={pointId} canManage={canManage} onClose={onClose} onSaved={onSaved} />, document.body);
}

/** `tz` = org timezone for the chart tooltip (the caller owns the store). `variant="compact"` (floating Live Map panel): one short row per point with a sparkline. Default is the larger chart. */
export default function ColdChainCard({ deviceId, className = 'mx-3 mb-3', variant = 'full', tz = null }) {
  const compactMode = variant === 'compact';
  const [openPointId, setOpenPointId] = useState(null);
  const { t } = useTranslation();
  const { temperature } = useFeatures();
  // On the Live Map the page-level provider feeds this card (one request for the whole page);
  // elsewhere (the device drawer) it fetches for itself.
  const shared = useColdChainShared();
  // Replies are tagged with what they were fetched for, so another vehicle's (or point's) data is
  // never shown under the current one while its own request is pending.
  const [pointsState, setPointsState] = useState({ deviceId: null, points: [] });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!temperature || !deviceId) return undefined;
    const timer = setInterval(() => setTick((n) => n + 1), REFRESH_MS);
    return () => clearInterval(timer);
  }, [temperature, deviceId]);

  useEffect(() => {
    if (shared || !temperature || !deviceId) return undefined;
    let cancelled = false;
    coldChain.overview()
      .then((r) => { if (!cancelled) setPointsState({ deviceId, points: (r?.points ?? []).filter((p) => p.deviceId === deviceId) }); })
      .catch(() => {});   // a failed refresh keeps the last good data for this device
    return () => { cancelled = true; };
  }, [shared, temperature, deviceId, tick]);

  // Derived synchronously from the shared source, so another vehicle's points can never linger.
  const points = !temperature ? []
    : shared ? (shared.byDevice.get(deviceId) ?? [])
      : (pointsState.deviceId === deviceId ? pointsState.points : []);
  // Full: the first point's chart. Compact: a sparkline for every point (a vehicle has a handful).
  const wantedIds = compactMode ? points.map((p) => p.id) : points.slice(0, 1).map((p) => p.id);
  const wantedKey = wantedIds.join('|');
  const [histories, setHistories] = useState({});
  useEffect(() => {
    if (!wantedKey) return undefined;
    let cancelled = false;
    const now = Date.now();
    const range = { from: new Date(now - 6 * 3_600_000).toISOString(), to: new Date(now).toISOString() };
    const ids = wantedKey.split('|');
    Promise.all(ids.map((id) => coldChain.history(id, range).catch(() => undefined)))
      .then((list) => {
        if (cancelled) return;
        // One state write; only this device's points are kept, a failed refresh keeps the last good chart.
        setHistories((prev) => Object.fromEntries(ids.map((id, i) => [id, list[i] ?? prev[id]]).filter(([, h]) => h)));
      });
    return () => { cancelled = true; };
  }, [wantedKey, tick]);

  const history = histories[wantedIds[0]] ?? null;
  const reloadAll = () => shared?.reload?.();
  if (!temperature || points.length === 0) return null;
  return (
    <section data-testid="cold-chain-card" className={`${className} rounded-2xl border border-slate-200 p-3 dark:border-slate-800`}>
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('coldChain.card.title', 'Cold chain')}</h4>
      <ul className="space-y-1">
        {points.map((p) => (compactMode ? (
          <li key={p.id} data-testid="cold-compact-row" className="flex items-center gap-2 text-sm">
            <button type="button" onClick={() => setOpenPointId(p.id)} aria-label={t('coldChain.chart.details', 'Open cold point details')}
              className="flex min-w-0 flex-1 items-center gap-2 rounded-lg text-start hover:bg-slate-50/60 dark:hover:bg-slate-800/40">
              <span className="w-28 shrink-0 truncate text-slate-700 dark:text-slate-200"><bdi>{p.name}</bdi></span>
              <bdi className="w-14 shrink-0 font-semibold tabular-nums">{p.status === 'no_data' ? '—' : formatTemp(p.value)}</bdi>
              {p.enabled && <ColdStatusChip status={p.status} lastReadingAt={p.lastReadingAt} />}
              <span className="h-10 min-w-0 flex-1">
                {histories[p.id] && <ColdPointChart history={histories[p.id]} height={40} compact interactive={false} tz={tz} />}
              </span>
            </button>
          </li>
        ) : (
          <li key={p.id} className="flex items-center justify-between gap-2 text-sm">
            <span className="truncate text-slate-700 dark:text-slate-200"><bdi>{p.name}</bdi></span>
            <span className="flex items-center gap-2">
              <bdi className="font-semibold tabular-nums">{p.status === 'no_data' ? '—' : formatTemp(p.value)}</bdi>
              {p.enabled && <ColdStatusChip status={p.status} lastReadingAt={p.lastReadingAt} />}
            </span>
          </li>
        )))}
      </ul>
      {!compactMode && history && <div className="mt-2"><ColdPointChart history={history} height={90} compact tz={tz} onActivate={shared ? () => setOpenPointId(points[0].id) : null} /></div>}
      {openPointId && <DrawerHost pointId={openPointId} onClose={() => setOpenPointId(null)} onSaved={reloadAll} />}
    </section>
  );
}
